import { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getGfaFromStoreys } from '@shared/helpers/design/shape.helpers';
import { getShapeCenter } from '@shared/helpers/design/cutout.helpers';
import { computePolygonArea } from '@shared/helpers/geometry/geometry.helpers';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import type { Point2D } from '@shared/models/geometry.model';
import type { Coordinate } from '@shared/models/geography.model';
import type { Shape, Storey } from '@shared/models/design/storey.model';
import { getStoreyElevationRange } from '@shared/helpers/design/storey.helpers';
import { shouldShowBuildingGfaLabel } from '../helpers/location-pin-visibility.helpers';
import type { DesignMap } from '../map';
import { Icon } from '@ui/Icon';
import { Tooltip } from '@ui/Tooltip';
import { formatSegmentLengthMeters } from '../helpers/segment-length-label.helpers';
import {
  FACADE_DIMENSION_LABEL_LINE_HEIGHT_PX,
  getFacadeBuildingDimensions,
  getFacadeDimensionLabels,
  type FacadeDimensionLabel,
} from '../helpers/facade-dimension-label.helpers';

const useStyles = makeStyles()(() => ({
  label: {
    position: 'absolute',
    zIndex: 2,
    color: 'rgba(0, 0, 0, 0.7)',
    fontFamily: 'Roboto, sans-serif',
    fontSize: 12,
    fontWeight: 500,
    lineHeight: `${FACADE_DIMENSION_LABEL_LINE_HEIGHT_PX}px`,
    pointerEvents: 'none',
    textShadow:
      '0 0 5px #fff, -2px 0 0 #fff, 2px 0 0 #fff, 0 -2px 0 #fff, 0 2px 0 #fff',
    transform: 'translate(-50%, -50%)',
    whiteSpace: 'nowrap',
  },
  dimensionLabel: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    transform: 'translate(0, -50%)',
  },
}));

const formatGfa = (gfa: number): string =>
  `${Math.round(gfa).toLocaleString('sv-SE').replaceAll('\u00a0', ' ')} m²`;

const FACADE_LABEL_OFFSET_PX = 24;

const getLabelCenter = (storeys: Storey[]): Point2D | undefined => {
  const largestShape = storeys
    .flatMap((storey) => storey.shapes)
    .filter((shape) => shape.cutout !== true && shape.points.length >= 3)
    .reduce<Shape | undefined>(
      (largest, shape) =>
        !largest ||
        computePolygonArea(shape.points) > computePolygonArea(largest.points)
          ? shape
          : largest,
      undefined,
    );
  return largestShape ? getShapeCenter(largestShape) : undefined;
};

type SelectedBuildingGfaLabelProps = {
  map: DesignMap | undefined;
  location: Coordinate;
  storeys: Storey[];
  visible: boolean;
  facadeDimensionsVisible?: boolean;
};

export const SelectedBuildingGfaLabel = ({
  map,
  location,
  storeys,
  visible,
  facadeDimensionsVisible = false,
}: SelectedBuildingGfaLabelProps) => {
  const { classes, cx } = useStyles();
  const dimensions = useMemo(
    () => getFacadeBuildingDimensions(storeys),
    [storeys],
  );
  const [dimensionLabels, setDimensionLabels] = useState<
    FacadeDimensionLabel[]
  >([]);
  const center = useMemo(() => getLabelCenter(storeys), [storeys]);
  const lowestElevation = useMemo(
    () =>
      storeys.reduce(
        (lowest, storey) =>
          Math.min(lowest, getStoreyElevationRange(storeys, storey).bottom),
        Number.POSITIVE_INFINITY,
      ),
    [storeys],
  );
  const [screenPoint, setScreenPoint] = useState<Point2D>();
  const [isLocationPinVisible, setIsLocationPinVisible] = useState(
    () => map?.state.locationPinMode$.getValue() ?? false,
  );
  const showLabel = shouldShowBuildingGfaLabel({
    overlayVisible: visible || facadeDimensionsVisible,
    isLocationPinVisible,
  });

  useEffect(() => {
    if (!map) {
      setIsLocationPinVisible(false);
      return;
    }
    const subscription = map.state.locationPinMode$.subscribe(
      setIsLocationPinVisible,
    );
    return () => subscription.unsubscribe();
  }, [map]);

  useEffect(() => {
    const mapInstance = map?.map;
    if (!mapInstance || !showLabel || !center) {
      setScreenPoint(undefined);
      setDimensionLabels([]);
      return;
    }
    const updatePosition = () => {
      setDimensionLabels(
        facadeDimensionsVisible && map.isExactFacadeMode
          ? getFacadeDimensionLabels({
              dimensions,
              project: (point, elevation) =>
                map.projectFacadeCoordinate(
                  convertMetricOffsetToCoordinate(location, point),
                  elevation,
                ),
            })
          : [],
      );
      const coordinate = convertMetricOffsetToCoordinate(location, center);
      const facadePoint = Number.isFinite(lowestElevation)
        ? map.projectFacadeCoordinate(coordinate, lowestElevation)
        : undefined;
      if (facadePoint) {
        setScreenPoint([
          facadePoint[0],
          facadePoint[1] + FACADE_LABEL_OFFSET_PX,
        ]);
        return;
      }
      const point = mapInstance.project([coordinate.lng, coordinate.lat]);
      setScreenPoint([point.x, point.y]);
    };
    updatePosition();
    mapInstance.on('move', updatePosition);
    mapInstance.on('render', updatePosition);
    mapInstance.on('resize', updatePosition);
    return () => {
      mapInstance.off('move', updatePosition);
      mapInstance.off('render', updatePosition);
      mapInstance.off('resize', updatePosition);
    };
  }, [
    center,
    location,
    lowestElevation,
    map,
    showLabel,
    facadeDimensionsVisible,
    dimensions,
  ]);

  if (!showLabel || !screenPoint) return null;

  return (
    <>
      {visible && (
        <div
          className={classes.label}
          style={{ left: screenPoint[0], top: screenPoint[1] }}
        >
          {formatGfa(getGfaFromStoreys(storeys))}
        </div>
      )}
      {dimensionLabels.map((label) => (
        <Tooltip
          key={label.kind}
          title={
            label.kind === 'height'
              ? 'Building height above ground'
              : 'Building depth below ground'
          }
          describeChild
        >
          <div
            className={cx(classes.label, classes.dimensionLabel)}
            style={{ left: label.screenPoint[0], top: label.screenPoint[1] }}
            aria-label={
              label.kind === 'height'
                ? 'Building height above ground'
                : 'Building depth below ground'
            }
          >
            <Icon
              category="mui"
              icon={label.kind === 'height' ? 'ArrowUpward' : 'ArrowDownward'}
              size={12}
            />
            <span>{formatSegmentLengthMeters(label.meters)} m</span>
          </div>
        </Tooltip>
      ))}
    </>
  );
};
